import type {
    ComponentStageData,
    MobileAlgoPlugin,
    MobileAlgoPluginInitializationArgs,
    PluginContext,
    PluginHeuristicsRegistry
} from '../../types'
import {
    createGridHandlers,
    createStackHandlers,
    getContainerLayoutType,
    getLayoutModelHandlers,
    postStageOperation
} from './layoutModels'
import {getContainerColumnCountKey, getContainerRowCountKey} from './common'
import type {OrderPluginAPI} from './types'
import type {Pointer} from '@wix/document-services-types'
import {ORDERED_GROUPED_STRUCTURE_HASH_KEY, HEADER_SPECIAL_CASE_KEY} from './constants'

export const createPlugin = ({
    stages,
    readOnlyExtensionAPI,
    logger
}: MobileAlgoPluginInitializationArgs): MobileAlgoPlugin => {
    const gridHandlers = createGridHandlers(readOnlyExtensionAPI)
    const stackHandlers = createStackHandlers()
    const handlersMap = {gridHandlers, stackHandlers}

    const getModeHandlers = (componentStageData: ComponentStageData, ctx: PluginContext) => {
        const containerLayoutType = getContainerLayoutType(componentStageData)
        return getLayoutModelHandlers(
            containerLayoutType,
            handlersMap,
            logger,
            componentStageData.componentPointer.id,
            ctx
        )[componentStageData.config.mode]
    }

    const register = (registry: PluginHeuristicsRegistry) => {
        registry.register(stages.GROUPING_ORDER, (data, ctx) =>
            getModeHandlers(data, ctx).createOrderVirtualStructure(data, ctx)
        )

        registry.register(stages.STRUCTURE_ORDER, (data, ctx) =>
            getModeHandlers(data, ctx).createOrderRealStructure(data, ctx)
        )

        registry.register(stages.POSITION, (data, ctx) => getModeHandlers(data, ctx).positionItems(data, ctx))

        registry.registerPostStageOperation(stages.STRUCTURE_ORDER, postStageOperation)
    }

    const createApi = (context: PluginContext): OrderPluginAPI => {
        return {
            getContainerRowCount: (containerPointer: Pointer): number | undefined =>
                context.get(getContainerRowCountKey(containerPointer)),
            getContainerColumnCount: (containerPointer: Pointer): number | undefined =>
                context.get(getContainerColumnCountKey(containerPointer)),
            getCalculatedHash: (): string | undefined => context.get(ORDERED_GROUPED_STRUCTURE_HASH_KEY),
            isHeaderSpecialCase: (): boolean => context.get(HEADER_SPECIAL_CASE_KEY) ?? false
        }
    }

    return {register, createApi, name: 'order', dependencies: []}
}
